import "./styles/main.scss";
import "leaflet/dist/leaflet.css";
import "bootstrap";

import L, {
  type Circle,
  type CircleMarker,
  type GeoJSON as GeoJsonLayer,
  type Map as LeafletMap,
} from "leaflet";
import type { Feature, FeatureCollection, Geometry } from "geojson";

import { currentAiracDate, distanceToGeometryMetres, pointInGeometry } from "./geo";
import { assessLocation, zonePermission } from "./location";
import { evaluate } from "./rules/engine";
import { cardHeading, groupCards, present, summarise } from "./rules/present";
import { cite, sources } from "./rules/sources";
import type {
  AirspaceMetadata,
  Card,
  FlightInput,
  LocationStatus,
  LocationVerdict,
  Severity,
} from "./types";
import {
  type ZoneCategory,
  type ZoneLimits,
  zoneCategories,
  zoneCategory,
  zoneLimits,
} from "./zones";

type BooleanField =
  | "camera"
  | "indoor"
  | "a2cofc"
  | "lowSpeed"
  | "overPeople"
  | "crowd"
  | "night"
  | "operatorOnly"
  | "remoteIdExempt";

/** Each card level carries a text label and symbol, so colour is never the only cue. */
const cardLevels: Readonly<Record<Severity, { variant: string; label: string }>> = {
  danger: { variant: "danger", label: "✕ Not permitted" },
  action: { variant: "warning", label: "! Action needed" },
  success: { variant: "success", label: "✓ OK" },
  warning: { variant: "dark", label: "▲ Important" },
  info: { variant: "light", label: "i Info" },
};

/** No restriction found is labelled as such, never as clearance to fly. */
const locationStatuses: Readonly<Record<LocationStatus, { label: string; icon: string }>> = {
  restricted: { label: "Restricted", icon: "bi-x-octagon-fill" },
  boundary: { label: "Uncertain — near a zone boundary", icon: "bi-exclamation-triangle-fill" },
  clear: { label: "Clear of permanent zones", icon: "bi-check-circle-fill" },
  unknown: { label: "Unknown — data stale or missing", icon: "bi-question-circle-fill" },
};

const UK_CENTRE: L.LatLngExpression = [54.6, -3.2];
/** Below this zoom, zones are drawn faintly and a hint asks the user to zoom in. */
const DETAIL_ZOOM = 9;

interface ZoneProperties {
  [key: string]: unknown;
  name?: string;
  description?: string;
}

type Zone = Feature<Geometry, ZoneProperties>;
type Zones = FeatureCollection<Geometry, ZoneProperties>;

interface ZoneDetail {
  name: string;
  category: ZoneCategory;
  limits: ZoneLimits | null;
  description: string;
}

/** Owns browser integration; legal decisions remain in the pure rules module. */
class DroneRulesApp {
  private readonly form = element<HTMLFormElement>("#rules-form");
  private readonly isAirspaceStale: boolean;
  private map?: LeafletMap;
  private marker?: CircleMarker;
  private accuracyCircle?: Circle;
  private zoneLayers = new Map<ZoneCategory, GeoJsonLayer>();
  private zoomHint?: HTMLElement;

  private constructor(
    private readonly zones: Zones,
    private readonly metadata: AirspaceMetadata,
  ) {
    this.isAirspaceStale =
      metadata.status !== "verified" || metadata.effectiveDate !== currentAiracDate();
  }

  /** Loads all dated assets before constructing the UI to avoid mixed-data results. */
  static async create(): Promise<DroneRulesApp> {
    const [zones, metadata] = await Promise.all([
      fetchJson<Zones>("/data/airspace.geojson"),
      fetchJson<AirspaceMetadata>("/data/airspace-metadata.json"),
    ]);
    return new DroneRulesApp(zones, metadata);
  }

  initialise(): void {
    this.renderDates();
    this.restoreState();
    this.form.addEventListener("input", (event) => {
      sanitiseNumeric(event.target);
      this.renderRules();
      this.saveState();
    });
    element("#locate").addEventListener("click", () => this.requestLocation());
    element("#coordinate-form").addEventListener("submit", (event) => {
      event.preventDefault();
      this.checkLocation(numberValue("#latitude"), numberValue("#longitude"));
    });
    this.initialiseChecklists();
    this.initialisePrinting();
    this.initialiseSummaryChip();
    this.renderRules();
    this.renderInitialLocation();
    this.initialiseMap();
    void this.locateIfPermitted();
  }

  private renderDates(): void {
    const caa = formatDate(sources.verifiedAsOf);
    const nats = this.isAirspaceStale
      ? `${formatDate(this.metadata.effectiveDate)} (out of date)`
      : formatDate(this.metadata.effectiveDate);
    for (const node of document.querySelectorAll(".js-caa-date")) node.textContent = caa;
    for (const node of document.querySelectorAll(".js-nats-date")) node.textContent = nats;
  }

  private renderRules(): void {
    try {
      const presentation = present(evaluate(this.readFlightInput()));
      const { subcategory, cards } = presentation;
      element("#result-title").textContent = subcategory
        ? `You're operating under ${subcategory}`
        : "Review required";
      element("#result-badge").textContent = subcategory ?? "NOT DECIDED";
      element("#rules-results").innerHTML = resultGroups(cards);
      const summary = summarise(presentation);
      element("#summary-chip-text").textContent = summary;
      element("#print-summary").textContent =
        `${summary}. Printed ${formatDate(new Date().toISOString().slice(0, 10))}; CAA guidance checked ${formatDate(sources.verifiedAsOf)}.`;
    } catch (error) {
      element("#result-title").textContent =
        error instanceof Error ? error.message : "Unable to evaluate";
    }
  }

  private readFlightInput(): FlightInput {
    const fields = this.form.elements;
    const checked = (name: BooleanField): boolean => checkbox(fields, name).checked;
    return {
      aircraft: {
        mass: numericField(fields, "mass"),
        camera: checked("camera"),
        classMark: valueField(fields, "classMark"),
        remoteIdExempt: checked("remoteIdExempt"),
      },
      pilot: {
        age: numericField(fields, "age"),
        a2cofc: checked("a2cofc"),
        operatorOnly: checked("operatorOnly"),
      },
      flight: {
        indoor: checked("indoor"),
        night: checked("night"),
        peopleDistance: numericField(fields, "peopleDistance"),
        builtDistance: numericField(fields, "builtDistance"),
        buildingDistance: numericField(fields, "buildingDistance"),
        overPeople: checked("overPeople"),
        crowd: checked("crowd"),
        lowSpeed: checked("lowSpeed"),
      },
    };
  }

  /** Answers live in the query string so a setup can be bookmarked or shared. */
  private restoreState(): void {
    const params = new URLSearchParams(window.location.search);
    for (const field of formFields(this.form)) {
      const value = params.get(field.name);
      if (value === null) continue;
      if (field instanceof HTMLInputElement && field.type === "checkbox")
        field.checked = value === "1";
      else if (field instanceof HTMLSelectElement) {
        if ([...field.options].some((option) => option.value === value)) field.value = value;
      } else field.value = value.replace(/\D/g, "");
    }
  }

  /** Only answers that differ from the defaults are written, keeping URLs short. */
  private saveState(): void {
    const params = new URLSearchParams();
    for (const field of formFields(this.form)) {
      if (field instanceof HTMLInputElement && field.type === "checkbox") {
        if (field.checked !== field.defaultChecked)
          params.set(field.name, field.checked ? "1" : "0");
      } else if (field.value !== defaultValue(field)) params.set(field.name, field.value);
    }
    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", url);
  }

  private initialiseChecklists(): void {
    for (const counter of document.querySelectorAll<HTMLElement>("[data-checklist]")) {
      const list = element(`#${counter.dataset.checklist ?? ""}`);
      const boxes = [...list.querySelectorAll<HTMLInputElement>("input[type=checkbox]")];
      const update = () => {
        const done = boxes.filter(({ checked }) => checked).length;
        counter.textContent = `${done} of ${boxes.length} checked`;
        counter.classList.toggle("check-count--complete", done === boxes.length);
      };
      list.addEventListener("change", update);
      update();
    }
  }

  /** Collapsed groups are opened for printing so the sheet is complete. */
  private initialisePrinting(): void {
    element("#print").addEventListener("click", () => window.print());
    let closed: HTMLDetailsElement[] = [];
    window.addEventListener("beforeprint", () => {
      closed = [...document.querySelectorAll<HTMLDetailsElement>("details:not([open])")];
      for (const details of closed) details.open = true;
    });
    window.addEventListener("afterprint", () => {
      for (const details of closed) details.open = false;
    });
  }

  /** Shows the current result while the inputs are on screen but the result is not. */
  private initialiseSummaryChip(): void {
    if (!("IntersectionObserver" in window)) return;
    const chip = element("#summary-chip");
    const visible = { requirements: false, result: false };
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target.id === "requirements") visible.requirements = entry.isIntersecting;
        if (entry.target.id === "result") visible.result = entry.isIntersecting;
      }
      chip.hidden = !(visible.requirements && !visible.result);
    });
    observer.observe(element("#requirements"));
    observer.observe(element("#result"));
  }

  private renderInitialLocation(): void {
    if (!this.isAirspaceStale) return;
    this.setLocationResult({
      status: "unknown",
      title: "No location result",
      message: this.staleMessage(),
    });
  }

  private staleMessage(): string {
    return this.metadata.status === "placeholder"
      ? "Airspace data unavailable — no location determination can be made. A verified NATS dataset must be ingested before deployment."
      : `Airspace data may be stale: effective ${formatDate(this.metadata.effectiveDate)}; expected ${formatDate(currentAiracDate())}. Check the NATS UAS restriction zones directly.`;
  }

  private initialiseMap(): void {
    const map = L.map("map", { minZoom: 5, preferCanvas: true, scrollWheelZoom: false });
    this.map = map;
    map.setView(UK_CENTRE, 6);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    // Scroll-wheel zoom only once the map is in use, so it never traps page scroll.
    map.on("focus click", () => map.scrollWheelZoom.enable());
    map.on("blur", () => map.scrollWheelZoom.disable());
    map.on("click", ({ latlng }) => this.checkLocation(latlng.lat, latlng.lng, 0, false));

    const byCategory = new Map<ZoneCategory, Zone[]>();
    for (const feature of this.zones.features) {
      const category = featureCategory(feature);
      byCategory.set(category, [...(byCategory.get(category) ?? []), feature]);
    }
    for (const category of Object.keys(zoneCategories) as ZoneCategory[]) {
      const layer = L.geoJSON(
        { type: "FeatureCollection", features: byCategory.get(category) ?? [] } as Zones,
        { style: zoneStyle(category, map.getZoom()) },
      ).addTo(map);
      this.zoneLayers.set(category, layer);
    }
    this.addLegend(map);
    this.addZoomHint(map);
    map.on("zoomend", () => {
      for (const [category, layer] of this.zoneLayers)
        layer.setStyle(zoneStyle(category, map.getZoom()));
      this.zoomHint?.classList.toggle("d-none", map.getZoom() >= DETAIL_ZOOM);
    });
  }

  /** The legend doubles as layer toggles. */
  private addLegend(map: LeafletMap): void {
    const legend = new L.Control({ position: "bottomleft" });
    legend.onAdd = () => {
      // A collapsible key, open by default only where the map has room for it.
      const container = L.DomUtil.create("details", "map-legend leaflet-bar");
      container.open = map.getSize().x >= 600;
      container.innerHTML = `<summary>Restriction zones</summary><fieldset><legend class="visually-hidden">Show restriction zones</legend>${(
        Object.entries(zoneCategories) as [ZoneCategory, (typeof zoneCategories)[ZoneCategory]][]
      )
        .map(
          ([category, { label, colour, dashArray }]) =>
            `<label><input type="checkbox" value="${category}" checked /><svg width="22" height="12" aria-hidden="true"><rect x="1" y="1" width="20" height="10" fill="${colour}" fill-opacity="0.25" stroke="${colour}" stroke-width="2" ${dashArray ? `stroke-dasharray="${dashArray}"` : ""}/></svg>${escapeHtml(label)}</label>`,
        )
        .join("")}</fieldset>`;
      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);
      container.addEventListener("change", (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        const layer = this.zoneLayers.get(input.value as ZoneCategory);
        if (!layer) return;
        if (input.checked) layer.addTo(map);
        else layer.remove();
      });
      return container;
    };
    legend.addTo(map);
  }

  private addZoomHint(map: LeafletMap): void {
    const hint = new L.Control({ position: "topright" });
    hint.onAdd = () => {
      const container = L.DomUtil.create("div", "map-hint");
      container.textContent = "Zoom in, use your location, or tap the map to check a point.";
      this.zoomHint = container;
      return container;
    };
    hint.addTo(map);
  }

  /** If location was granted on an earlier visit, start at the user's area. */
  private async locateIfPermitted(): Promise<void> {
    try {
      const permission = await navigator.permissions.query({ name: "geolocation" });
      if (permission.state === "granted") this.requestLocation();
    } catch {
      // Permissions API unavailable: wait for the button.
    }
  }

  private requestLocation(): void {
    if (!("geolocation" in navigator)) {
      this.showUnavailable("Geolocation is not supported by this browser.");
      return;
    }
    this.setLocationResult({
      title: "Waiting for your location…",
      message: "Your browser will ask before sharing the position with this page.",
    });
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => this.checkLocation(coords.latitude, coords.longitude, coords.accuracy),
      ({ message }) => this.showUnavailable(`Location unavailable: ${message}`),
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 30_000 },
    );
  }

  /** Checks the point locally; the verdict itself comes from the rules module. */
  private checkLocation(latitude: number, longitude: number, accuracy = 0, recentre = true): void {
    if (
      !Number.isFinite(latitude) ||
      !Number.isFinite(longitude) ||
      latitude < 49 ||
      latitude > 61 ||
      longitude < -9 ||
      longitude > 3
    ) {
      this.showUnavailable("Enter coordinates within the United Kingdom.");
      return;
    }

    const point = [longitude, latitude] as const;
    const nearest = this.zones.features
      .map((feature) => ({
        feature,
        inside: pointInGeometry(point, feature.geometry),
        distance: distanceToGeometryMetres(point, feature.geometry),
      }))
      .sort((left, right) => left.distance - right.distance);
    const inside = nearest.filter(({ inside }) => inside).map(({ feature }) => zoneDetail(feature));
    const verdict = assessLocation({
      airspaceStale: this.isAirspaceStale,
      effectiveDate: this.metadata.effectiveDate && formatDate(this.metadata.effectiveDate),
      accuracy,
      zonesInside: inside.map(({ name, category }) => ({
        name,
        type: zoneCategories[category].type,
      })),
      nearestDistance: nearest[0]?.distance ?? null,
    });
    const shown =
      verdict.status === "restricted"
        ? inside
        : verdict.status === "boundary" && nearest[0]
          ? [zoneDetail(nearest[0].feature)]
          : [];

    this.placeMarker(latitude, longitude, accuracy, verdict, recentre);
    this.setLocationResult({
      ...verdict,
      message: verdict.status === "unknown" ? this.staleMessage() : verdict.message,
      zones: shown,
      position: `${latitude.toFixed(5)}, ${longitude.toFixed(5)}${accuracy > 0 ? ` (±${Math.round(accuracy)}m)` : ""}`,
    });
  }

  private placeMarker(
    latitude: number,
    longitude: number,
    accuracy: number,
    verdict: LocationVerdict,
    recentre: boolean,
  ): void {
    if (!this.map) return;
    if (recentre) this.map.setView([latitude, longitude], 13);
    this.marker?.remove();
    this.accuracyCircle?.remove();
    const colour = themeColour(verdict.level === "info" ? "dark" : verdict.level);
    if (accuracy > 0) {
      this.accuracyCircle = L.circle([latitude, longitude], {
        radius: accuracy,
        color: themeColour("primary"),
        fillOpacity: 0.12,
        interactive: false,
      }).addTo(this.map);
    }
    this.marker = L.circleMarker([latitude, longitude], {
      radius: 9,
      color: "#fff",
      weight: 3,
      fillColor: colour,
      fillOpacity: 1,
    })
      .bindPopup(
        `<strong>${escapeHtml(locationStatuses[verdict.status].label)}</strong><br />Details are shown above the map.`,
      )
      .addTo(this.map)
      .openPopup();
  }

  private showUnavailable(message: string): void {
    this.setLocationResult({ title: "No location result", message });
  }

  /** Without a status the panel stays neutral, for prompts and input errors. */
  private setLocationResult(result: {
    title: string;
    message: string;
    status?: LocationStatus;
    zones?: ZoneDetail[];
    position?: string;
  }): void {
    const panel = element("#location-result");
    panel.className = `location-result location-result--${result.status ?? "idle"}`;
    const status = result.status ? locationStatuses[result.status] : null;
    const source = cite("restrictionZones");
    const dataset = this.metadata.sourceUrl
      ? `<a href="${escapeHtml(this.metadata.sourceUrl)}" target="_blank" rel="noopener">NATS dataset effective ${escapeHtml(formatDate(this.metadata.effectiveDate))} ↗</a>`
      : "";
    panel.innerHTML = `
      <p class="location-result__status">${status ? `<i class="bi ${status.icon}" aria-hidden="true"></i> ${escapeHtml(status.label)}` : "Location check"}</p>
      <h3 class="location-result__title">${escapeHtml(result.title)}</h3>
      <p>${escapeHtml(result.message)}</p>
      ${result.zones?.length ? `<ul class="zone-list">${result.zones.map(zoneItem).join("")}</ul>` : ""}
      ${result.position ? `<p class="small mb-2">Checked point: ${escapeHtml(result.position)}</p>` : ""}
      ${
        result.status
          ? `<p class="location-result__links small mb-0"><a href="${source.url}" target="_blank" rel="noopener">${escapeHtml(source.label)} ↗</a>${dataset}</p>`
          : ""
      }`;
  }
}

function featureCategory(feature: Zone): ZoneCategory {
  return zoneCategory(feature.properties.name ?? "", feature.properties.description);
}

function zoneDetail(feature: Zone): ZoneDetail {
  return {
    name: feature.properties.name?.trim() ?? "Restricted area",
    category: featureCategory(feature),
    limits: zoneLimits(feature.properties.description),
    description: feature.properties.description ?? "",
  };
}

function zoneStyle(category: ZoneCategory, zoom: number): L.PathOptions {
  const { colour, dashArray } = zoneCategories[category];
  const detailed = zoom >= DETAIL_ZOOM;
  return {
    color: colour,
    fillColor: colour,
    weight: detailed ? 2 : zoom >= 7 ? 1 : 0.5,
    opacity: detailed ? 0.9 : 0.6,
    fillOpacity: detailed ? 0.15 : 0.25,
    dashArray: detailed ? dashArray : undefined,
  };
}

function zoneItem({ name, category, limits, description }: ZoneDetail): string {
  const { type, colour } = zoneCategories[category];
  const vertical = limits
    ? `${limits.lower} to ${limits.upper}`
    : "See the zone's entry in UK AIP ENR 5.1";
  const aip = cite("aip");
  return `<li class="zone-list__item" style="--zone-colour: ${colour}">
    <strong>${escapeHtml(name)}</strong>
    <span class="zone-list__type">${escapeHtml(type)}</span>
    <dl class="zone-list__facts">
      <dt>Vertical limits</dt><dd>${escapeHtml(vertical)}</dd>
      <dt>Permission</dt><dd>${escapeHtml(zonePermission(type))}</dd>
    </dl>
    ${description ? `<details><summary>Full AIP entry</summary><p class="small mb-0">${escapeHtml(description)}</p></details>` : ""}
    <a class="small" href="${aip.url}" target="_blank" rel="noopener">${escapeHtml(aip.label)} ↗</a>
  </li>`;
}

/** Reads a Bootstrap theme colour so map layers follow any swapped-in theme. */
function themeColour(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(`--bs-${name}`).trim();
}

function resultGroups(cards: Card[]): string {
  const { action, flight, always } = groupCards(cards);
  const grid = (items: Card[]) =>
    `<div class="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">${items.map(resultCard).join("")}</div>`;
  const actions =
    action.length > 0
      ? grid(action)
      : `<p class="text-secondary">Nothing to register or fit for this flight.</p>`;
  return `
    <h3 class="h4 mb-3">What you must do</h3>${actions}
    ${flight.length > 0 ? `<h3 class="h4 mt-5 mb-3">This flight</h3>${grid(flight)}` : ""}
    ${always.length > 0 ? `<details class="always-applies mt-5"><summary class="h4">Always applies (${always.length})</summary>${grid(always)}</details>` : ""}`;
}

function resultCard(item: Card): string {
  const { variant, label } = cardLevels[item.level];
  const note = item.note
    ? `<p class="small text-danger-emphasis mt-2 mb-0">${escapeHtml(item.note)}</p>`
    : "";
  return `<article class="col"><div class="card h-100 result-card result-card--${variant}"><div class="card-body"><span class="badge text-bg-${variant} mb-2">${escapeHtml(label)}</span><h4 class="h5 card-title">${escapeHtml(cardHeading(item))}</h4><p class="card-text">${escapeHtml(item.text)}</p>${note}</div><div class="card-footer bg-transparent border-0"><a class="small fw-semibold" href="${item.citation.url}" target="_blank" rel="noopener">${escapeHtml(item.citation.label)} ↗</a></div></div></article>`;
}

function element<T extends Element = HTMLElement>(selector: string): T {
  const match = document.querySelector<T>(selector);
  if (!match) throw new Error(`Required element not found: ${selector}`);
  return match;
}

function formFields(form: HTMLFormElement): (HTMLInputElement | HTMLSelectElement)[] {
  return [...form.elements].filter(
    (field): field is HTMLInputElement | HTMLSelectElement =>
      (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) &&
      field.name !== "",
  );
}

function defaultValue(field: HTMLInputElement | HTMLSelectElement): string {
  if (field instanceof HTMLInputElement) return field.defaultValue;
  const options = [...field.options];
  return (options.find(({ defaultSelected }) => defaultSelected) ?? options[0])?.value ?? "";
}

/** Numeric text inputs accept digits only; the unit is shown inline. */
function sanitiseNumeric(target: EventTarget | null): void {
  if (!(target instanceof HTMLInputElement) || target.inputMode !== "numeric") return;
  const digits = target.value.replace(/\D/g, "");
  if (digits !== target.value) target.value = digits;
}

function valueField(fields: HTMLFormControlsCollection, name: string): string {
  const field = fields.namedItem(name);
  if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement))
    throw new Error(`Missing form field: ${name}`);
  return field.value;
}

function numericField(fields: HTMLFormControlsCollection, name: string): number {
  return Number(valueField(fields, name));
}

function checkbox(fields: HTMLFormControlsCollection, name: string): HTMLInputElement {
  const field = fields.namedItem(name);
  if (!(field instanceof HTMLInputElement) || field.type !== "checkbox")
    throw new Error(`Missing checkbox: ${name}`);
  return field;
}

/** Accepts a typed minus or the Unicode minus sign. */
function numberValue(selector: string): number {
  const value = element<HTMLInputElement>(selector).value.trim().replace("−", "-");
  return value === "" ? Number.NaN : Number(value);
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Could not load ${url} (${response.status})`);
  return response.json() as Promise<T>;
}

function formatDate(value: string | null): string {
  if (!value) return "unknown";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(`${value}T12:00:00Z`));
}

function escapeHtml(value: string): string {
  const entities: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    '"': "&quot;",
  };
  return value.replace(/[&<>'"]/g, (character) => entities[character] ?? character);
}

document.documentElement.dataset.app = "loading";
void DroneRulesApp.create()
  .then((app) => {
    app.initialise();
    document.documentElement.dataset.app = "ready";
  })
  .catch((error: unknown) => {
    console.error(error);
    document.documentElement.dataset.app = "failed";
    element("#app-error").classList.remove("d-none");
    element("#result-title").textContent = "Unavailable";
    element("#location-result").textContent =
      "No location result: the restriction data could not be loaded.";
  });
